import { create } from 'zustand'
import type {
  SessionState,
  SessionStateData,
  DeepPartial,
  CameraDevice,
  PrinterDevice,
  LogEntry
} from '@renderer/types/sessionState'
import { useNavigate } from 'react-router-dom'
import { useConfigStore } from './configState'
import { stateUploader } from '@renderer/utils/pipeline/outputOrchestrator'
import { Filter, FrameOption } from '@renderer/types/configState'
import { fetchLUTData } from '@renderer/utils/lutUtils'
import { useSettingsStore } from './settingsStore'
import { useAuthStore } from './authStore'

type PreloadApi = {
  saveActivityLogs?: (
    sessionId: string,
    logs: Array<{
      message: string
      timestamp: Date
      level: 'info' | 'warn' | 'error'
      page?: string
      details?: Record<string, unknown>
    }>
  ) => Promise<void>
  saveSession?: (
    sessionData: Record<string, unknown>
  ) => Promise<{ success: boolean; error?: string }>
  getInternetStatus?: () => Promise<boolean>
  triggerSync?: () => Promise<{ success: boolean }>
}

const getPreloadApi = (): PreloadApi | undefined => {
  if (typeof window === 'undefined') return undefined
  return (window as typeof window & { api?: PreloadApi }).api
}

/**
 * Check real network connectivity status
 * Uses window.api.getInternetStatus() if available (Electron), otherwise falls back to navigator.onLine
 */
async function checkNetworkStatus(): Promise<boolean> {
  if (typeof window === 'undefined') return true // Assume online in SSR
  const api = getPreloadApi()
  if (api?.getInternetStatus) {
    return await api.getInternetStatus()
  }
  return navigator.onLine // Fallback to browser API
}

/**
 * Waits for the settings store to be hydrated from localStorage.
 * Zustand's persist middleware loads asynchronously, so we need to wait
 * for it to complete before reading persisted values like selectedPrinter.
 *
 * @param maxWaitMs Maximum time to wait in milliseconds (default: 5000ms)
 * @returns Promise that resolves when settings are hydrated
 */
async function waitForSettingsHydration(maxWaitMs = 5000): Promise<void> {
  const startTime = Date.now()

  return new Promise((resolve) => {
    const checkSettings = (): void => {
      const settings = useSettingsStore.getState()

      // deviceId is always set (either from localStorage or generated),
      // so if it exists, the store has been initialized
      // For hydration check, we look for a persisted value or the generated default
      // If deviceId exists and it's not the default empty string, we're hydrated
      const isHydrated = settings.deviceId && settings.deviceId.length > 0

      if (isHydrated) {
        resolve()
        return
      }

      // Check timeout
      if (Date.now() - startTime > maxWaitMs) {
        console.warn('[SETTINGS] Timeout waiting for settings hydration. Proceeding anyway.', {
          deviceId: settings.deviceId
        })
        resolve() // Resolve anyway to not block initialization
        return
      }

      // Check again after a short delay
      setTimeout(checkSettings, 50)
    }

    // Start checking immediately
    checkSettings()
  })
}

interface User {
  _id: string
  email: string
  username: string
}

interface InitializeParams {
  projectId: string
  user: User
  eventId?: string
}

/** Loads filters, frames, and stickers for a project into the config store (no session state changes). */
export async function loadProjectCatalogFromApi(projectId: string): Promise<{
  configuredFilters: Filter[]
  configuredFrames: FrameOption[]
  photoRatio: number | null
}> {
  const [filterResponse, frameResponse, stickerResponse] = await Promise.all([
    fetch(`${process.env.VITE_API_URL}/api/filters?projectId=${projectId}`, {
      credentials: 'include',
      cache: 'no-cache'
    }),
    fetch(`${process.env.VITE_API_URL}/api/frames?projectId=${projectId}`, {
      credentials: 'include',
      cache: 'no-cache'
    }),
    fetch(`${process.env.VITE_API_URL}/api/stickers?projectId=${projectId}`, {
      credentials: 'include',
      cache: 'no-cache'
    })
  ])

  if (!filterResponse.ok) {
    throw new Error(`${filterResponse.statusText || 'Failed to fetch filters'}`)
  }
  if (!frameResponse.ok) {
    throw new Error(`${frameResponse.statusText || 'Failed to fetch frames'}`)
  }
  if (!stickerResponse.ok) {
    throw new Error(`${stickerResponse.statusText || 'Failed to fetch stickers'}`)
  }

  const configuredFilters: Filter[] = await filterResponse.json()
  const frameData = await frameResponse.json()
  const configuredStickers = await stickerResponse.json()

  const lutFilterIds = configuredFilters
    .filter((f) => f.filterMode === 'lut' && f.lutData?.size)
    .map((f) => f._id)
  if (lutFilterIds.length > 0) {
    await Promise.all(lutFilterIds.map((id) => fetchLUTData(id)))
  }

  const configuredFrames: FrameOption[] = []

  if (frameData.categoryModeEnabled) {
    frameData.frameCategories.forEach((category: { frames: FrameOption[] }) => {
      category.frames.forEach((frame) => {
        if (!configuredFrames.some((f) => f._id === frame._id)) {
          configuredFrames.push(frame)
        }
      })
    })
    useConfigStore.getState().setFrameCategories(frameData.frameCategories)
  } else {
    configuredFrames.push(...frameData.frames)
  }

  await useConfigStore.getState().setFilters(configuredFilters)
  useConfigStore.getState().setFrames(configuredFrames)

  const editSettings = useConfigStore.getState().getPageSettings('edit')
  const stickerIds = editSettings?.stickerIds || []
  const filteredStickers = Array.isArray(configuredStickers)
    ? configuredStickers.filter((sticker) => stickerIds.includes(sticker._id))
    : []
  useConfigStore.getState().setStickers(filteredStickers)

  const initialFrame: FrameOption | undefined = configuredFrames[0]
  const photoImage = initialFrame?.drawParams?.images?.find((img) =>
    img.type.startsWith('photo-')
  )
  const photoRatio = photoImage ? photoImage.width / photoImage.height : null

  return { configuredFilters, configuredFrames, photoRatio }
}

// Update SessionState type to include new methods
interface ExtendedSessionState extends SessionStateData {
  sessionUploadError: string | null
  setSessionUploadError: (error: string | null) => void
  initializeSession: (params: InitializeParams) => Promise<void>
  refreshProjectCatalog: (projectId: string) => Promise<void>
  revokeGalleryUrls: () => Promise<void>
  uploadSessionState: (targetSessionId?: string, stateSnapshot?: DeepPartial<SessionStateData> | null) => Promise<void>
  endSession: () => void
  cleanupSession: () => Promise<void>
  setSessionState: (update: DeepPartial<SessionStateData>) => void
  logActivity: (
    message: string,
    details?: Record<string, unknown>,
    pageFromClick?: string,
    sessionId?: string
  ) => void
}

// Session-scoped log storage
interface SessionLogs {
  [sessionId: string]: LogEntry[]
}

const sessionLogs: SessionLogs = {}

// Queue for managing session updates
class SessionUpdateQueue {
  private queue: Array<() => Promise<void>> = []
  private isProcessing = false

  async add(updateFn: () => Promise<void>): Promise<void> {
    this.queue.push(updateFn)
    if (!this.isProcessing) {
      await this.process()
    }
  }

  getIsProcessing(): boolean {
    return this.isProcessing
  }

  private async process(): Promise<void> {
    if (this.isProcessing || this.queue.length === 0) return

    this.isProcessing = true
    while (this.queue.length > 0) {
      const updateFn = this.queue.shift()
      if (updateFn) {
        try {
          await updateFn()
        } catch (error) {
          console.error('Error processing session update:', error)
          // Keep failed update in queue for retry
          this.queue.unshift(updateFn)
          break
        }
      }
    }
    this.isProcessing = false
  }
}

const sessionUpdateQueue = new SessionUpdateQueue()

// Debounce timer for batch log syncing
let logSyncTimeout: NodeJS.Timeout | null = null

/**
 * Saves activity logs to SQLite storage via IPC
 * @param sessionId - Current session ID
 * @param logsMap - Map of session logs by session ID
 */
async function saveActivityLogsToStorage(sessionId: string, logsMap: SessionLogs): Promise<void> {
  const api = getPreloadApi()
  if (!api?.saveActivityLogs) {
    return
  }

  const allSessionLogs = logsMap[sessionId] || []
  if (allSessionLogs.length === 0) {
    return
  }

  // Convert to format expected by API
  const logsToSave = allSessionLogs.map((log) => ({
    message: log.message,
    timestamp: log.timestamp,
    level: (log.level || 'info') as 'info' | 'warn' | 'error',
    page: log.page,
    details: log.details || {}
  }))

  // @ts-ignore - saveActivityLogs exists on window.api at runtime
  await api.saveActivityLogs(sessionId, logsToSave)

  sessionLogs[sessionId] = []
}

/**
 * Debounced version of saveActivityLogsToStorage to batch multiple log saves
 * This reduces overhead and ensures complete log sets are uploaded together
 * @param sessionId - Current session ID
 * @param logsMap - Map of session logs by session ID
 */
function debouncedSaveActivityLogs(sessionId: string, logsMap: SessionLogs): void {
  if (!getPreloadApi()?.saveActivityLogs) {
    return
  }

  // Clear existing timeout
  if (logSyncTimeout) {
    clearTimeout(logSyncTimeout)
  }

  // Set new timeout to batch logs
  logSyncTimeout = setTimeout(() => {
    saveActivityLogsToStorage(sessionId, logsMap).catch((error) => {
      console.error('[ACTIVITY LOGS] Failed to save to SQLite:', error)
    })
  }, 2000) // Wait 2 seconds of inactivity before syncing
}

/**
 * Upload activity logs directly to API
 */
async function uploadActivityLogsToApi(
  sessionId: string,
  logs: LogEntry[]
): Promise<{ success: boolean; error?: string }> {
  try {
    const authState = useAuthStore.getState()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const token = authState.user?.cookie || await (window as any).api?.getAuthToken?.()

    const payload = {
      sessionId,
      logs: logs.map((log) => ({
        message: log.message,
        timestamp: log.timestamp,
        level: log.level || 'info',
        page: log.page,
        details: log.details || {}
      }))
    }

    // Electron: bearer from auth store or IPC. Web: `user.cookie` is unset; NextAuth cookies are sent with credentials: 'include' (see resolveAuthSession on API).
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(`${process.env.VITE_API_URL || ''}/api/activity-logs`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      const errorText = await response.text()
      return { success: false, error: `API error: ${response.status} ${errorText}` }
    }

    return { success: true }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    }
  }
}

const getInitialState = (): SessionStateData => {
  return {
    id: '',
    projectId: '',
    userId: '',
    eventId: undefined,
    userEmail: undefined,
    mode: undefined,
    timeline: {
      appLaunchTime: new Date(),
      startTime: null,
      endTime: null,
      duration: null,
      isCompleted: false
    },
    userPreferences: {
      filterId: null,
      frameId: null,
      copies: 0,
      retakeCount: 0,
      retakeSessionsCompleted: 0,
      framePhotoRatio: null,
      useGIF: true,
      useRecording: true,
      useVideo: true,
      useDigital: true,
      usePrint: true,
      usePax: false,
      selectedCamera: 'primary',
      email: '',
      phoneNumber: '',
      satisfactionRating: null,
      satisfactionTimestamp: null
    },
    userConsent: {
      photoPublication: false,
      consentTimestamp: new Date()
    },
    devices: {
      camera: null,
      printer: null
    },
    transaction: {
      orderId: null,
      amount: 0,
      discount: 0,
      qrUrl: null,
      expiryTime: null,
      serverTime: null,
      status: 'pending',
      extraPrintPaymentStatus: undefined,
      selectedPackages: [],
      printed: false,
      extraPrintAmount: 0,
      extraPrintCopies: 0
    },
    gallery: {
      id: '',
      stripUrl: null,
      capturedItems: [],
      filterCache: {},
      liveModeUrl: null,
      liveModeProcessing: false
    },
    galleryKey: '',
    logs: [] as LogEntry[]
  }
}

const logStateChange = (update: DeepPartial<SessionStateData>): void => {
  if (process.env.NODE_ENV === 'development') {
    const changes: string[] = []

    if (update.timeline) {
      if (update.timeline.startTime) changes.push('session started')
      if (update.timeline.endTime) changes.push('session ended')
      if (update.timeline.isCompleted !== undefined)
        changes.push(`session completion status changed to ${update.timeline.isCompleted}`)
    }

    if (update.userPreferences) {
      if (update.userPreferences.filterId !== undefined)
        changes.push(`filter changed to ${update.userPreferences.filterId}`)
      if (update.userPreferences.frameId !== undefined)
        changes.push(`frame changed to ${update.userPreferences.frameId}`)
      if (update.userPreferences.copies !== undefined)
        changes.push(`copies set to ${update.userPreferences.copies}`)
      if (update.userPreferences.retakeCount !== undefined)
        changes.push(`retake count set to ${update.userPreferences.retakeCount}`)
      if (update.userPreferences.useGIF !== undefined)
        changes.push(`GIF usage set to ${update.userPreferences.useGIF}`)
      if (update.userPreferences.useRecording !== undefined)
        changes.push(`recording usage set to ${update.userPreferences.useRecording}`)
      if (update.userPreferences.useVideo !== undefined)
        changes.push(`video usage set to ${update.userPreferences.useVideo}`)
      if (update.userPreferences.selectedCamera !== undefined)
        changes.push(`selected camera changed to ${update.userPreferences.selectedCamera}`)
    }

    if (update.devices) {
      if (update.devices.camera !== undefined)
        changes.push(`camera device updated to ${update.devices.camera?.name || 'null'}`)
      if (update.devices.printer !== undefined)
        changes.push(`printer device updated to ${update.devices.printer?.name || 'null'}`)
    }

    if (update.transaction) {
      if (update.transaction.status !== undefined)
        changes.push(`transaction status changed to ${update.transaction.status}`)
      if (update.transaction.amount !== undefined)
        changes.push(`transaction amount set to ${update.transaction.amount}`)
      if (update.transaction.orderId !== undefined)
        changes.push(`transaction order ID set to ${update.transaction.orderId}`)
    }

    if (update.gallery) {
      if (update.gallery.capturedItems && update.gallery.capturedItems.length > 0)
        changes.push(`captured ${update.gallery.capturedItems.length} items`)
      if (update.gallery.liveModeUrl !== undefined)
        changes.push(`live mode URL set to ${update.gallery.liveModeUrl}`)
      if (update.gallery.liveModeProcessing !== undefined)
        changes.push(`live mode processing status set to ${update.gallery.liveModeProcessing}`)
    }
    if (update.galleryKey !== undefined) changes.push(`galleryKey set to ${update.galleryKey}`)

    const logMessage = `Session state updated:\n${changes.map((change) => `- ${change}`).join('\n')}.`
    console.log(logMessage)
  }
}

export const useSessionStateStore = create<ExtendedSessionState>((set, get) => ({
  ...getInitialState(),
  sessionUploadError: null,
  setSessionUploadError: (error: string | null): void => set({ sessionUploadError: error }),
  initializeSession: async ({ projectId, user, eventId }: InitializeParams): Promise<void> => {
    if (get().projectId === projectId) {
      return
    }
    useConfigStore.getState().resetLoadingProgress()
    try {
      // Wait for settings store to be hydrated before reading persisted values
      // This prevents race conditions where initializeSession runs before
      // localStorage data is loaded into the store
      await waitForSettingsHydration()

      const { configuredFilters, configuredFrames, photoRatio } =
        await loadProjectCatalogFromApi(projectId)

      // Get current state for fallback logic
      const currentState = get()

      // Get default devices from settings store (now guaranteed to be hydrated)
      const { selectedCamera, selectedPrinter } = useSettingsStore.getState()

      // Fallback logic: if device is missing from settings, try to preserve from previous session
      // This handles cases where settings might have been cleared but we want to maintain
      // device continuity across sessions
      const cameraToUse = selectedCamera || currentState.devices?.camera?.id
      const printerToUse = selectedPrinter || currentState.devices?.printer?.id

      // Log warnings if devices are missing (for debugging)
      if (!selectedPrinter && currentState.devices?.printer) {
        console.warn(
          '[INIT] Printer not found in settings store, using previous session printer:',
          {
            previousPrinter: currentState.devices.printer.name,
            settingsStoreState: {
              selectedPrinter: useSettingsStore.getState().selectedPrinter,
              deviceId: useSettingsStore.getState().deviceId
            }
          }
        )
      } else if (!selectedPrinter && !currentState.devices?.printer) {
        console.warn('[INIT] No printer found in settings store or previous session')
      }

      if (!selectedCamera && currentState.devices?.camera) {
        console.warn('[INIT] Camera not found in settings store, using previous session camera:', {
          previousCamera: currentState.devices.camera.name
        })
      }

      // Initialize session state with all fetched data and default devices
      set((state) => ({
        ...state,
        projectId,
        userId: user._id,
        eventId,
        userPreferences: {
          ...state.userPreferences,
          filterId: configuredFilters[0]?._id,
          frameId: configuredFrames[0]?._id,
          framePhotoRatio: photoRatio,
          selectedCamera: 'primary',
          satisfactionRating: null,
          satisfactionTimestamp: null
        },
        devices: {
          camera: cameraToUse
            ? ({
                id: cameraToUse,
                name: cameraToUse,
                status: 'ready',
                type: 'camera'
              } as CameraDevice)
            : null,
          printer: printerToUse
            ? ({
                id: printerToUse,
                name: printerToUse,
                status: 'ready'
              } as PrinterDevice)
            : null
        }
      }))
    } catch (error) {
      console.error('Error initializing session:', error)
      throw new Error(`${(error as Error).message || 'Failed to initialize session'}`)
    }
  },
  refreshProjectCatalog: async (projectId: string): Promise<void> => {
    useConfigStore.getState().resetLoadingProgress()
    await waitForSettingsHydration()
    await loadProjectCatalogFromApi(projectId)
  },
  logActivity: (
    message: string,
    details?: Record<string, unknown>,
    pageFromClick?: string,
    sessionId?: string
  ): void => {
    const currentSessionId = sessionId || get().id
    const hasActivitySync = Boolean(getPreloadApi()?.saveActivityLogs)

    let page = pageFromClick
    if (!page) {
      const pathParts = window.location.href.split('/').filter(Boolean)
      page = pathParts.pop() || 'welcome'
    }
    console.log(
      `${message} [${details?.to ? `to ${details.to} page` : `in ${page} page`}]`,
      '\n',
      details || ''
    )

    const logEntry: LogEntry = {
      message,
      details,
      page: page,
      level: 'info',
      timestamp: new Date(),
      ...(currentSessionId ? { sessionId: currentSessionId } : {})
    }

    set((state) => ({
      logs: [...state.logs, logEntry]
    }))

    if (!currentSessionId) {
      return
    }

    // Store in session-scoped storage
    if (!sessionLogs[currentSessionId]) {
      sessionLogs[currentSessionId] = []
    }
    sessionLogs[currentSessionId].push(logEntry)

    // Debounced batch upload: try direct API first, fall back to SQLite only on failure.
    // A single debounce timer ensures only ONE path fires per batch window.
    if (logSyncTimeout) clearTimeout(logSyncTimeout)

    logSyncTimeout = setTimeout(async () => {
      const logsToSend = sessionLogs[currentSessionId] || []
      if (logsToSend.length === 0) return

      const isOnline = await checkNetworkStatus()
      if (isOnline) {
        const result = await uploadActivityLogsToApi(currentSessionId, logsToSend)
        if (result.success) {
          sessionLogs[currentSessionId] = []
          return
        }
        console.warn('[ActivityLog] Direct upload failed, falling back to SQLite:', result.error)
      }

      // Offline or direct upload failed — persist to SQLite (syncManager will upload later)
      if (hasActivitySync) {
        debouncedSaveActivityLogs(currentSessionId, sessionLogs)
      }
    }, 10000)
  },
  revokeGalleryUrls: async (): Promise<void> => {
    // Import backgroundVideoProcessor to check for active tasks
    const { backgroundVideoProcessor } = await import('@renderer/utils/backgroundVideoProcessor')

    // Check if there are any active video processing tasks that might need these blob URLs
    const hasActiveVideoTasks = backgroundVideoProcessor.hasActiveVideoTasks()

    if (hasActiveVideoTasks) {
      console.log('Delaying blob URL cleanup - video processing tasks are still active')
      set((state) => ({
        logs: [
          ...state.logs,
          {
            message: 'Delaying blob URL cleanup - video processing tasks are still active',
            details: { hasActiveVideoTasks },
            page: 'sessionState',
            level: 'info',
            timestamp: new Date()
          }
        ]
      }))

      // Wait for video processing to complete before cleaning up
      const waitForVideoCompletion = async (): Promise<void> => {
        return new Promise((resolve) => {
          const checkInterval = setInterval(() => {
            const stillProcessing = backgroundVideoProcessor.hasActiveVideoTasks()

            if (!stillProcessing) {
              clearInterval(checkInterval)
              console.log('Video processing completed - proceeding with blob URL cleanup')
              set((state) => ({
                logs: [
                  ...state.logs,
                  {
                    message: 'Video processing completed - proceeding with blob URL cleanup',
                    details: {},
                    page: 'sessionState',
                    level: 'info',
                    timestamp: new Date()
                  }
                ]
              }))
              resolve()
            }
          }, 1000)

          // Timeout after 30 seconds to prevent indefinite waiting
          setTimeout(() => {
            clearInterval(checkInterval)
            console.log(
              'Timeout waiting for video processing - proceeding with blob URL cleanup anyway'
            )
            set((state) => ({
              logs: [
                ...state.logs,
                {
                  message:
                    'Timeout waiting for video processing - proceeding with blob URL cleanup anyway',
                  details: {},
                  page: 'sessionState',
                  level: 'warn',
                  timestamp: new Date()
                }
              ]
            }))
            resolve()
          }, 30000)
        })
      }

      await waitForVideoCompletion()
    }

    // Get current state for cleanup
    const currentState = get()

    // Cleanup gallery URLs
    currentState.gallery.capturedItems?.forEach((item) => {
      if (item.photoUrl && item.photoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(item.photoUrl)
      }
      if (item.videoUrl && item.videoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(item.videoUrl)
      }
    })

    console.log('Blob URL cleanup completed')
    set((state) => ({
      logs: [
        ...state.logs,
        {
          message: 'Blob URL cleanup completed',
          details: {},
          page: 'sessionState',
          level: 'info',
          timestamp: new Date()
        }
      ]
    }))
  },
  endSession: (): void => {
    set((state) => ({
      ...state,
      timeline: {
        ...state.timeline,
        endTime: new Date(),
        duration: state.timeline.startTime
          ? new Date().getTime() - state.timeline.startTime.getTime()
          : null
      }
    }))
  },
  uploadSessionState: async (
    targetSessionId?: string,
    stateSnapshot?: DeepPartial<SessionStateData> | null
  ): Promise<void> => {
    await sessionUpdateQueue.add(async () => {
      try {
        await stateUploader.uploadSessionState(
          targetSessionId,
          stateSnapshot as Record<string, unknown> | null | undefined
        )
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err)
        if (message.startsWith('NON_TRANSIENT:')) {
          set({ sessionUploadError: message })
        }
        throw err
      }
    })
  },
  cleanupSession: async (): Promise<void> => {
    const currentState = get()
    
    // Wait for critical operations with 2s timeout
    const criticalChecks: Promise<void>[] = [
      // Check session upload queue
      new Promise<void>((resolve) => {
        let checks = 0
        const checkInterval = setInterval(() => {
          checks++
          if (!sessionUpdateQueue.getIsProcessing() || checks >= 20) {  // 20 * 100ms = 2s
            clearInterval(checkInterval)
            resolve()
          }
        }, 100)
      }),
      // Check video processing (reuse existing pattern from revokeGalleryUrls)
      (async (): Promise<void> => {
        const { backgroundVideoProcessor } = await import('@renderer/utils/backgroundVideoProcessor')
        let checks = 0
        return new Promise<void>((resolve) => {
          const checkInterval = setInterval(() => {
            checks++
            if (!backgroundVideoProcessor.hasActiveVideoTasks() || checks >= 20) {
              clearInterval(checkInterval)
              resolve()
            }
          }, 100)
        })
      })()
    ]
    
    await Promise.race([
      Promise.all(criticalChecks),
      new Promise<void>((resolve) => setTimeout(resolve, 2000))  // Max 2s wait
    ])
    
    const appLaunchTime = currentState.timeline.appLaunchTime
    const devices = currentState.devices
    const projectId = currentState.projectId
    useConfigStore.getState().resetLoadingProgress()

    // Reset state but keep app launch time and project ID
    set({
      ...getInitialState(),
      projectId,
      devices,
      timeline: {
        ...getInitialState().timeline,
        appLaunchTime
      },
      userPreferences: {
        ...getInitialState().userPreferences,
        selectedCamera: 'primary',
        satisfactionRating: null,
        satisfactionTimestamp: null
      },
      logs: []
    })
  },
  setSessionState: (update: DeepPartial<SessionStateData>): void => {
    set((state) => {
      const newState = {
        ...state,
        ...(update as Partial<SessionState>),
        timeline: {
          ...state.timeline,
          ...(update.timeline || {})
        },
        userPreferences: {
          ...state.userPreferences,
          ...(update.userPreferences || {})
        },
        userConsent: {
          ...state.userConsent,
          ...(update.userConsent || {})
        },
        devices: {
          ...state.devices,
          ...(update.devices || {})
        },
        transaction: {
          ...state.transaction,
          ...(update.transaction || {})
        },
        gallery: {
          ...state.gallery,
          ...(update.gallery || {})
        },
        galleryKey:
          update.galleryKey !== undefined
            ? update.galleryKey
            : update.gallery?.id !== undefined
              ? update.gallery.id
              : state.galleryKey,
        logs: [...state.logs, ...(update.logs || [])]
      } as SessionState

      logStateChange(update)
      return newState
    })
  }
}))

// Custom hooks for different session management scenarios
export const useSessionReset = (): (() => Promise<void>) => {
  const navigate = useNavigate()
  const { projectId } = useSessionStateStore()
  const { uploadSessionState, revokeGalleryUrls, cleanupSession } = useSessionStateStore()

  return async () => {
    // Fire-and-forget upload
    uploadSessionState().catch(() => {})
    await revokeGalleryUrls()
    await cleanupSession()
    navigate(`/${projectId}`)
  }
}

export const useSessionCleanup = (): (() => Promise<void>) => {
  const navigate = useNavigate()
  const { projectId } = useSessionStateStore()
  const { revokeGalleryUrls, cleanupSession } = useSessionStateStore()

  return async () => {
    await revokeGalleryUrls()
    await cleanupSession()
    navigate(`/${projectId}`)
  }
}
